import { r, s, typographyTokens } from '@/theme/tokens';
import { resolveChartPanelSurfaceBackgroundColorImportant } from './chartPanelSurfaceTokens';
import type { SxProps, Theme } from '@/ui/primitives';

type ThemeSxObject = Exclude<SxProps<Theme>, readonly unknown[] | ((theme: Theme) => unknown)>;

type ChartPanelTitleJustification = 'flex-start' | 'center';

type BuildChartPanelTitleSxArgs = {
  fontSizePx: number;
  fontWeight: number;
  justifyContent: ChartPanelTitleJustification;
};

type BuildMetricCardTitleSxArgs = {
  fontSize: string | { xs?: number; xl?: number };
  fontWeight: number;
};

export const chartPanelMiniMetricEmptyStateContentSx: ThemeSxObject = {
  transform: 'translateY(-6px)',
};

export const chartPanelTitleTextSx: ThemeSxObject = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const chartPanelTitleInfoIconSx: ThemeSxObject = {
  flexShrink: 0,
  fontSize: 17,
  color: 'inherit',
  transform: 'translateY(1px)',
};

export const metricCardTitleInfoIconSx: ThemeSxObject = {
  flexShrink: 0,
  fontSize: 11,
  color: 'inherit',
  transform: 'translateY(-1px)',
};

export const metricInfoPopoverBodySx: ThemeSxObject = {
  px: s('xl'),
  pt: s('xl'),
  pb: s('2xl'),
};

export const chartPanelGridContentSx: ThemeSxObject = {
  display: 'grid',
  alignItems: 'stretch',
};

export const chartPanelColumnContentSx: ThemeSxObject = {
  display: 'flex',
  flexDirection: 'column',
};

export const buildChartPanelTitleSx = ({
  fontSizePx,
  fontWeight,
  justifyContent,
}: BuildChartPanelTitleSxArgs): ThemeSxObject => ({
  m: s('none'),
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent,
  gap: s('sm'),
  color: (theme) => theme.app.colors.textMuted,
  fontSize: (muiTheme) => muiTheme.typography.pxToRem(fontSizePx),
  fontWeight,
  lineHeight: 1.25,
  whiteSpace: 'nowrap',
});

export const buildMetricCardTitleSx = ({
  fontSize,
  fontWeight,
}: BuildMetricCardTitleSxArgs): ThemeSxObject => ({
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  gap: s('sm'),
  color: (theme) => theme.app.colors.textMuted,
  fontSize,
  fontWeight,
  lineHeight: 1.3,
  whiteSpace: 'nowrap',
});

type ChartPanelCenteredStateLayout = 'flex' | 'grid';

type BuildChartPanelCenteredStateSxArgs = {
  height: number;
  minHeight?: number;
  layout?: ChartPanelCenteredStateLayout;
  offsetY?: number;
  widthFull?: boolean;
};

type BuildMiniMetricChartEmptyStateRecipeArgs = {
  height: number;
};

type MiniMetricChartEmptyStateRecipe = {
  containerSx: ThemeSxObject;
  contentSx: ThemeSxObject;
};

type StatusChartPanelStateRecipe = {
  containerSx: ThemeSxObject;
};

type BuildMetricChartPanelHeaderSxArgs = {
  headerHeight: number;
  headerPaddingBottom: string | number;
  headerPaddingTop: string | number;
  headerPaddingX: string | number;
};

type BuildMetricsChartPanelFrameHeaderSxArgs = {
  headerPaddingBottom: string;
  headerPaddingTop: string;
  headerPaddingX: string;
};

type BuildMetricsChartPanelFrameContentSxArgs = {
  contentPaddingBottom: string;
  contentPaddingTop: string;
  contentPaddingX: string;
};

type BuildMetricChartPanelBodySxArgs = {
  panelPaddingBottom: string | number;
  panelPaddingTop: string | number;
};

type BuildMetricChartPanelOuterPaddingSxArgs = {
  panelPaddingBottom: string | number;
  panelPaddingTop: string | number;
};

type BuildMetricChartPanelContentSxArgs = {
  contentPaddingBottom: string | number;
  contentPaddingTop: string | number;
  contentPaddingX: string | number;
  centred?: boolean;
};

type BuildStatusPieBreakdownLayoutRecipeArgs = {
  centreOffsetY: string;
  legendMarginTop: string;
  legendSlotHeight: number;
  visualSize: number;
};

type StatusPieBreakdownLayoutRecipe = {
  panelBodySx: ThemeSxObject;
  chartSlotSx: ThemeSxObject;
  chartVisualFrameSx: ThemeSxObject;
  centreOverlaySx: ThemeSxObject;
  centreValueStackSx: ThemeSxObject;
  legendSlotSx: ThemeSxObject;
};

type BuildStatusStackedLegendLayoutRecipeArgs = {
  fontSizePx: number;
  lineHeight: number;
  swatchOffsetY: string;
};

type BuildStatusStackedChartHostSxArgs = {
  axisLabelFontSize: string;
  axisLabelFontWeight: number;
  axisLabelOpacity: number;
  paddingBottom: string;
  paddingTop: string;
  paddingX: string;
};

type StatusStackedLegendLayoutRecipe = {
  headerSlotSx: ThemeSxObject;
  legendSx: ThemeSxObject;
  itemSx: ThemeSxObject;
  swatchSx: ThemeSxObject;
  labelSx: ThemeSxObject;
};

export const metricChartPanelFixedSurfaceInsetSx: ThemeSxObject = {
  display: 'flex',
  flexDirection: 'column',
  px: s('md'),
};

export const metricsChartPanelFrameSurfaceSx: ThemeSxObject = {
  display: 'flex',
  flexDirection: 'column',
};

export const buildChartPanelCenteredStateSx = ({
  height,
  minHeight,
  layout = 'flex',
  offsetY = 0,
  widthFull = false,
}: BuildChartPanelCenteredStateSxArgs): ThemeSxObject => {
  const baseSx: ThemeSxObject = {
    height,
    display: layout,
    transform: offsetY === 0 ? undefined : `translateY(${String(offsetY)}px)`,
  };

  if (widthFull) {
    baseSx.width = '100%';
  }

  if (minHeight !== undefined) {
    baseSx.minHeight = minHeight;
  }

  if (layout === 'grid') {
    baseSx.placeItems = 'center';
    return baseSx;
  }

  baseSx.alignItems = 'center';
  baseSx.justifyContent = 'center';
  return baseSx;
};

export const buildMiniMetricChartEmptyStateRecipe = ({
  height,
}: BuildMiniMetricChartEmptyStateRecipeArgs): MiniMetricChartEmptyStateRecipe => ({
  containerSx: buildChartPanelCenteredStateSx({ height }),
  contentSx: chartPanelMiniMetricEmptyStateContentSx,
});

export const buildStatusChartPanelStateRecipe = (
  args: BuildChartPanelCenteredStateSxArgs,
): StatusChartPanelStateRecipe => ({
  containerSx: buildChartPanelCenteredStateSx(args),
});

export const buildMetricChartPanelHeaderSx = ({
  headerHeight,
  headerPaddingBottom,
  headerPaddingTop,
  headerPaddingX,
}: BuildMetricChartPanelHeaderSxArgs): ThemeSxObject => ({
  minHeight: `${String(headerHeight)}px`,
  height: `${String(headerHeight)}px`,
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  px: headerPaddingX,
  pt: headerPaddingTop,
  pb: headerPaddingBottom,
  boxSizing: 'border-box',
  flex: '0 0 auto',
});

export const buildMetricsChartPanelFrameHeaderSx = ({
  headerPaddingBottom,
  headerPaddingTop,
  headerPaddingX,
}: BuildMetricsChartPanelFrameHeaderSxArgs): ThemeSxObject => ({
  minWidth: 0,
  px: headerPaddingX,
  pt: headerPaddingTop,
  pb: headerPaddingBottom,
  boxSizing: 'border-box',
});

export const buildMetricsChartPanelFrameContentSx = ({
  contentPaddingBottom,
  contentPaddingTop,
  contentPaddingX,
}: BuildMetricsChartPanelFrameContentSxArgs): ThemeSxObject => ({
  flex: '1 1 auto',
  minHeight: 0,
  px: contentPaddingX,
  pt: contentPaddingTop,
  pb: contentPaddingBottom,
  boxSizing: 'border-box',
});

export const buildMetricChartPanelBodySx = ({
  panelPaddingBottom,
  panelPaddingTop,
}: BuildMetricChartPanelBodySxArgs): ThemeSxObject => ({
  pt: panelPaddingTop,
  pb: panelPaddingBottom,
  display: 'flex',
  flex: '1 1 auto',
  minHeight: 0,
  flexDirection: 'column',
});

export const buildMetricChartPanelOuterPaddingSx = ({
  panelPaddingBottom,
  panelPaddingTop,
}: BuildMetricChartPanelOuterPaddingSxArgs): ThemeSxObject => ({
  pt: panelPaddingTop,
  pb: panelPaddingBottom,
});

export const buildMetricChartPanelContentSx = ({
  contentPaddingBottom,
  contentPaddingTop,
  contentPaddingX,
  centred = false,
}: BuildMetricChartPanelContentSxArgs): ThemeSxObject => ({
  px: contentPaddingX,
  pt: contentPaddingTop,
  pb: contentPaddingBottom,
  boxSizing: 'border-box',
  ...(centred
    ? {
        flex: '1 1 auto',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }
    : {}),
});

export const buildStatusPieBreakdownLayoutRecipe = ({
  centreOffsetY,
  legendMarginTop,
  legendSlotHeight,
  visualSize,
}: BuildStatusPieBreakdownLayoutRecipeArgs): StatusPieBreakdownLayoutRecipe => ({
  panelBodySx: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    height: '100%',
    minHeight: 0,
  },
  chartSlotSx: {
    display: 'grid',
    flex: '1 1 auto',
    placeItems: 'center',
    minHeight: 0,
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
  },
  chartVisualFrameSx: {
    height: `${String(visualSize)}px`,
    maxWidth: '100%',
    position: 'relative',
    width: `${String(visualSize)}px`,
  },
  centreOverlaySx: {
    position: 'absolute',
    inset: 0,
    display: 'grid',
    placeItems: 'center',
    pointerEvents: 'none',
    transform: `translateY(${centreOffsetY})`,
  },
  centreValueStackSx: {
    display: 'grid',
    justifyItems: 'center',
    gap: s('xxs'),
  },
  legendSlotSx: {
    display: 'flex',
    flex: '0 0 auto',
    minHeight: `${String(legendSlotHeight)}px`,
    mt: legendMarginTop === s('none') ? s('xs') : legendMarginTop,
    alignItems: 'flex-end',
    justifyContent: 'center',
    width: '100%',
  },
});

export const buildStatusStackedLegendLayoutRecipe = ({
  fontSizePx,
  lineHeight,
  swatchOffsetY,
}: BuildStatusStackedLegendLayoutRecipeArgs): StatusStackedLegendLayoutRecipe => ({
  headerSlotSx: {
    display: 'flex',
    justifyContent: 'flex-start',
    minWidth: 0,
    maxWidth: '100%',
  },
  legendSx: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: s('md'),
    rowGap: s('xs'),
    minWidth: 0,
    maxWidth: '100%',
  },
  itemSx: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: s('sm'),
    minWidth: 0,
  },
  swatchSx: {
    display: 'inline-flex',
    alignItems: 'center',
    transform: `translateY(${swatchOffsetY})`,
  },
  labelSx: {
    fontSize: (muiTheme) => muiTheme.typography.pxToRem(fontSizePx),
    fontWeight: typographyTokens.body.fontWeight,
    lineHeight,
    whiteSpace: 'nowrap',
  },
});

export const buildStatusStackedChartHostSx = ({
  axisLabelFontSize,
  axisLabelFontWeight,
  axisLabelOpacity,
  paddingBottom,
  paddingTop,
  paddingX,
}: BuildStatusStackedChartHostSxArgs): ThemeSxObject => ({
  flex: '1 1 auto',
  minHeight: 0,
  display: 'flex',
  px: paddingX,
  pt: paddingTop,
  pb: paddingBottom,
  boxSizing: 'border-box',
  '& .MuiChartsAxis-tickLabel': {
    fontSize: axisLabelFontSize,
    fontWeight: axisLabelFontWeight,
    opacity: axisLabelOpacity,
  },
});

export type MetricInfoPopoverPlacement = 'below' | 'left' | 'right';

type BuildMetricInfoPopoverPaperSxArgs = {
  placement: MetricInfoPopoverPlacement;
  viewportMarginPx: number;
  widthPx: number;
};

export const buildMetricInfoPopoverPaperSx = ({
  placement,
  viewportMarginPx,
  widthPx,
}: BuildMetricInfoPopoverPaperSxArgs): ThemeSxObject => ({
  ml: placement === 'left' ? `calc(-1 * ${s('lg')})` : placement === 'right' ? s('lg') : s('none'),
  mt: placement === 'below' ? s('md') : `calc(-1 * ${s('3xl')})`,
  width: `min(${String(widthPx)}px, calc(100vw - ${String(viewportMarginPx)}px))`,
  maxWidth: widthPx,
  borderWidth: '1px',
  borderRadius: r('lg'),
});

type BuildChartPanelSurfaceSxArgs = {
  width?: number;
  height?: number;
  heightMode?: 'height' | 'minHeight';
  overflowHidden?: boolean;
};

export const buildChartPanelSurfaceSx = ({
  width,
  height,
  heightMode = 'height',
  overflowHidden = true,
}: BuildChartPanelSurfaceSxArgs): Array<
  Exclude<SxProps<Theme>, readonly unknown[] | null | undefined>
> => {
  const sx: Array<Exclude<SxProps<Theme>, readonly unknown[] | null | undefined>> = [
    { backgroundColor: resolveChartPanelSurfaceBackgroundColorImportant },
  ];

  if (width !== undefined) {
    sx.push({
      width: `${width}px`,
      minWidth: `${width}px`,
      maxWidth: `${width}px`,
    });
  }

  if (height !== undefined) {
    sx.push(
      heightMode === 'minHeight'
        ? { minHeight: `${height}px` }
        : { height: `${height}px`, overflow: overflowHidden ? 'hidden' : undefined },
    );
  }

  return sx;
};
